import React from 'react';
import { cn, formatCurrency, formatWeight, formatHead, formatBags, formatPercent } from '@/lib/utils';

export type TabularType = 'currency' | 'weight' | 'head' | 'bags' | 'percent' | 'number';

interface TabularNumberProps {
  value: number | null | undefined;
  type?: TabularType;
  decimals?: number;
  isPercentDecimal?: boolean;
  className?: string;
  prefix?: string;
  suffix?: string;
  align?: 'left' | 'center' | 'right';
  highlightPositive?: boolean;
  highlightNegative?: boolean;
}

export const TabularNumber: React.FC<TabularNumberProps> = ({
  value,
  type = 'number',
  decimals = 2,
  isPercentDecimal = false,
  className,
  prefix = '',
  suffix = '',
  align = 'right',
  highlightPositive = false,
  highlightNegative = false,
}) => {
  let formattedText = '—';

  if (value !== null && value !== undefined && !isNaN(value)) {
    switch (type) {
      case 'currency':
        formattedText = formatCurrency(value);
        break;
      case 'weight':
        formattedText = formatWeight(value, decimals);
        break;
      case 'head':
        formattedText = formatHead(value);
        break;
      case 'bags':
        formattedText = formatBags(value);
        break;
      case 'percent':
        formattedText = formatPercent(value, isPercentDecimal);
        break;
      case 'number':
      default:
        formattedText = value.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
        break;
    }
  }

  const alignmentClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  const isPositive = (value ?? 0) > 0;
  const isNegative = (value ?? 0) < 0;

  const colorClass =
    highlightPositive && isPositive
      ? 'text-emerald-700 font-semibold'
      : highlightNegative && isNegative
      ? 'text-red-600 font-semibold'
      : '';

  return (
    <span
      className={cn(
        'font-mono tabular-nums inline-block select-all tracking-tight',
        alignmentClass,
        colorClass,
        className
      )}
    >
      {prefix}
      {formattedText}
      {suffix}
    </span>
  );
};
